import { useState } from "react";
import { ArrowRight, Mail, MessageCircle, Sparkles } from "lucide-react";
import {
  COMMERCIAL_EMAIL,
  COMMERCIAL_QR_SRC,
  COMMERCIAL_WHATSAPP_DISPLAY,
  commercialWhatsAppUrl,
} from "@/config/commercial";
import { cn } from "@/lib/utils";

interface CommercialContactCardProps {
  className?: string;
}

/**
 * Chamada para falar com o comercial (WhatsApp + QR code).
 * Visual escuro fixo, igual ao da apresentação comercial, nos dois temas.
 * O QR só aparece em telas médias+ (no celular o botão já abre o WhatsApp) e some
 * sozinho se a imagem falhar, sem quebrar o layout.
 */
export function CommercialContactCard({ className }: CommercialContactCardProps) {
  const [qrFailed, setQrFailed] = useState(false);
  const whatsappUrl = commercialWhatsAppUrl();

  return (
    <section
      aria-labelledby="commercial-cta-title"
      className={cn(
        "relative isolate overflow-hidden rounded-3xl border border-white/10 bg-[#0B1226] text-white shadow-2xl",
        className
      )}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 -top-32 -z-10 h-80 w-80 rounded-full bg-[radial-gradient(closest-side,rgba(62,116,245,0.40),transparent)]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-32 -left-24 -z-10 h-72 w-72 rounded-full bg-[radial-gradient(closest-side,rgba(139,124,249,0.25),transparent)]"
      />

      <div className="grid items-center gap-8 p-6 sm:p-10 md:grid-cols-[minmax(0,1fr)_auto]">
        <div className="min-w-0 space-y-5 text-left">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-slate-200">
            <Sparkles aria-hidden="true" className="h-3.5 w-3.5 text-[#8EB0FF]" />
            Condição especial
          </span>

          <h3
            id="commercial-cta-title"
            className="text-balance text-2xl font-extrabold leading-tight tracking-tight sm:text-3xl"
          >
            Contate nosso comercial agora e tenha um desconto de até{" "}
            <span className="text-[#8EB0FF]">100%</span>
          </h3>

          <p className="max-w-xl text-base text-slate-300">
            Fale com um especialista, veja o BaitaTicket funcionando e monte um plano sob medida para a sua operação.
          </p>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-[#2F63E0] px-6 py-3 text-base font-semibold text-white shadow-lg shadow-[#3E74F5]/30 transition-colors hover:bg-[#2757C9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B1226]"
            >
              <MessageCircle aria-hidden="true" className="h-5 w-5" />
              Falar com o comercial
              <ArrowRight aria-hidden="true" className="h-4 w-4" />
            </a>
            <a
              href={`mailto:${COMMERCIAL_EMAIL}`}
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl px-3 text-sm font-medium text-slate-300 underline-offset-4 transition-colors hover:text-white hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B1226]"
            >
              <Mail aria-hidden="true" className="h-4 w-4" />
              {COMMERCIAL_EMAIL}
            </a>
          </div>

          <p className="text-sm text-slate-400">WhatsApp {COMMERCIAL_WHATSAPP_DISPLAY}</p>
        </div>

        {!qrFailed && (
          <div className="hidden flex-col items-center gap-3 md:flex">
            <div className="rounded-2xl bg-white p-3 shadow-xl">
              <img
                src={COMMERCIAL_QR_SRC}
                alt={`QR code para falar com o comercial no WhatsApp ${COMMERCIAL_WHATSAPP_DISPLAY}`}
                width={160}
                height={160}
                loading="lazy"
                decoding="async"
                onError={() => setQrFailed(true)}
                className="h-40 w-40"
              />
            </div>
            <p className="max-w-[10rem] text-center text-xs text-slate-300">
              Aponte a câmera para falar no WhatsApp
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
